import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import type { Role } from '../types';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  roles?: Role[]; // if omitted, visible to all admin roles
  end?: boolean;
}

interface NavGroup {
  section: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    section: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: '📊', end: true }],
  },
  {
    section: 'Menu',
    items: [
      { to: '/categories', label: 'Categories', icon: '🗂️' },
      { to: '/products', label: 'Products', icon: '🍔' },
    ],
  },
  {
    section: 'Sales',
    items: [
      { to: '/bills', label: 'Bills', icon: '🧾', end: true },
      { to: '/coupons', label: 'Coupons', icon: '🏷️' },
      { to: '/reports/sales', label: 'Sales Reports', icon: '📈' },
      { to: '/reports/analytics', label: 'Product / Payment / Tax', icon: '📊' },
      { to: '/bills/cancelled', label: 'Cancelled Bills', icon: '🚫' },
      { to: '/bills/refunds', label: 'Refunds', icon: '↩️' },
    ],
  },
  {
    section: 'Administration',
    items: [
      { to: '/stores', label: 'Stores', icon: '🏬', roles: ['SUPER_ADMIN'] },
      { to: '/users', label: 'Users', icon: '👥', roles: ['SUPER_ADMIN'] },
      { to: '/settings', label: 'Settings', icon: '⚙️', roles: ['SUPER_ADMIN'] },
      { to: '/printers', label: 'Printer Config', icon: '🖨️' },
      { to: '/audit', label: 'Audit Logs', icon: '📜' },
    ],
  },
];

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  STORE_ADMIN: 'Store Admin',
  CASHIER: 'Cashier',
};

export function Layout() {
  const { user, logout } = useAuth();
  const { currentStore, canSwitchStore } = useStores();

  if (!user) return null;

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="logo">18</div>
          <div>
            <div className="brand-name">1885 NY</div>
            <div className="brand-sub">Smash Burgers</div>
          </div>
        </div>
        <nav className="nav">
          {NAV.map((group) => {
            const items = group.items.filter(
              (it) => !it.roles || it.roles.includes(user.role),
            );
            if (items.length === 0) return null;
            return (
              <div key={group.section}>
                <div className="nav-section">{group.section}</div>
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    className={({ isActive }) =>
                      `nav-link${isActive ? ' active' : ''}`
                    }
                  >
                    <span className="icon">{it.icon}</span>
                    <span>{it.label}</span>
                  </NavLink>
                ))}
              </div>
            );
          })}
        </nav>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="row gap-sm">
            <span className="badge badge-red">
              {canSwitchStore ? 'All Stores' : currentStore?.name ?? 'Store'}
            </span>
          </div>
          <div className="topbar-right">
            <div className="user-chip">
              <div className="user-avatar">{initials(user.fullName)}</div>
              <div className="user-meta">
                <div className="name">{user.fullName}</div>
                <div className="role">{ROLE_LABEL[user.role]}</div>
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={logout}>
              Sign out
            </button>
          </div>
        </header>

        <main className="page">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
